// MFA presentational screens: enrollment (QR), verification, and recovery codes.
import { Shield, Loader2, AlertCircle, Copy, Download, Check, Fingerprint } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

function CodeInput({ value, onChange, testId }) {
  return (
    <Input
      inputMode="numeric"
      autoComplete="one-time-code"
      placeholder="123456"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
      className="h-12 text-center text-2xl tracking-[0.4em] bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 focus:border-sky-500"
      data-testid={testId}
    />
  );
}

export function MfaSetup({ setupData, code, setCode, onSubmit, submitting, error }) {
  return (
    <div data-testid="mfa-setup-screen">
      <h2 className="text-xl font-semibold text-white mb-1">Set up two-factor authentication</h2>
      <p className="text-slate-400 text-sm mb-6">
        Scan the QR code with an authenticator app (Google Authenticator, Authy, or Microsoft Authenticator), then enter the 6-digit code to activate.
      </p>

      {error && (
        <div className="mb-5 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400" data-testid="mfa-error">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      <div className="flex justify-center mb-4">
        {setupData?.qr ? (
          <img src={setupData.qr} alt="MFA QR code" className="h-48 w-48 rounded-lg bg-white p-2" data-testid="mfa-qr" />
        ) : (
          <div className="h-48 w-48 rounded-lg bg-slate-700 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-sky-400" />
          </div>
        )}
      </div>

      {setupData?.secret && (
        <div className="mb-5 text-center">
          <p className="text-xs text-slate-500 mb-1">Can't scan? Enter this key manually:</p>
          <code className="text-sm text-sky-300 bg-slate-700/60 px-2 py-1 rounded break-all" data-testid="mfa-secret">{setupData.secret}</code>
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-4">
        <div className="space-y-2">
          <Label className="text-slate-300">Verification code</Label>
          <CodeInput value={code} onChange={setCode} testId="mfa-setup-code" />
        </div>
        <Button type="submit" disabled={submitting || code.length !== 6} className="w-full h-11 bg-sky-500 hover:bg-sky-600 text-white" data-testid="mfa-setup-submit">
          {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Shield className="mr-2 h-4 w-4" />}
          Activate &amp; continue
        </Button>
      </form>
    </div>
  );
}

export function MfaVerify({ code, setCode, onSubmit, submitting, error, useRecovery, setUseRecovery }) {
  return (
    <div data-testid="mfa-verify-screen">
      <h2 className="text-xl font-semibold text-white mb-1">Two-factor authentication</h2>
      <p className="text-slate-400 text-sm mb-6">
        {useRecovery ? 'Enter one of your recovery codes.' : 'Enter the 6-digit code from your authenticator app.'}
      </p>

      {error && (
        <div className="mb-5 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400" data-testid="mfa-error">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-4">
        <div className="space-y-2">
          <Label className="text-slate-300">{useRecovery ? 'Recovery code' : 'Verification code'}</Label>
          {useRecovery ? (
            <Input
              placeholder="xxxx-xxxx-xxxx"
              value={code}
              onChange={(e) => setCode(e.target.value.trim())}
              className="h-12 text-center bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 focus:border-sky-500"
              data-testid="mfa-recovery-input"
            />
          ) : (
            <CodeInput value={code} onChange={setCode} testId="mfa-verify-code" />
          )}
        </div>
        <Button type="submit" disabled={submitting || !code} className="w-full h-11 bg-sky-500 hover:bg-sky-600 text-white" data-testid="mfa-verify-submit">
          {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Verify &amp; sign in
        </Button>
      </form>

      <button
        type="button"
        onClick={() => { setUseRecovery(!useRecovery); setCode(''); }}
        className="mt-4 w-full text-center text-sm text-sky-400 hover:text-sky-300"
        data-testid="mfa-toggle-recovery"
      >
        {useRecovery ? 'Use authenticator app instead' : 'Use a recovery code'}
      </button>
    </div>
  );
}

export function PasskeyVerify({ onVerify, onUseCode, submitting, error }) {
  return (
    <div data-testid="passkey-verify-screen">
      <div className="flex justify-center mb-4">
        <div className="h-14 w-14 rounded-2xl bg-sky-500/15 flex items-center justify-center">
          <Fingerprint className="h-7 w-7 text-sky-400" />
        </div>
      </div>
      <h2 className="text-xl font-semibold text-white mb-1 text-center">Verify with your passkey</h2>
      <p className="text-slate-400 text-sm mb-6 text-center">
        Use your device's fingerprint, face, or PIN to finish signing in.
      </p>

      {error && (
        <div className="mb-5 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400" data-testid="passkey-error">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      <Button type="button" onClick={onVerify} disabled={submitting} className="w-full h-11 bg-sky-500 hover:bg-sky-600 text-white" data-testid="passkey-verify-submit">
        {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Fingerprint className="mr-2 h-4 w-4" />}
        Use passkey
      </Button>

      <button type="button" onClick={onUseCode} disabled={submitting}
        className="mt-4 w-full text-center text-sm text-slate-400 hover:text-slate-200 disabled:opacity-50"
        data-testid="passkey-use-code">
        Use authenticator code instead
      </button>
    </div>
  );
}

export function RecoveryCodes({ codes, onDone }) {
  const [copied, setCopied] = useState(false);
  const text = (codes || []).join('\n');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const download = () => {
    const blob = new Blob([`Latinos Salud Portal — Recovery Codes\n\n${text}\n`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'latinos-salud-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="mfa-recovery-screen">
      <h2 className="text-xl font-semibold text-white mb-1">Save your recovery codes</h2>
      <p className="text-slate-400 text-sm mb-5">
        Store these somewhere safe. Each code works once and lets you sign in if you lose access to your authenticator app.
      </p>

      <div className="grid grid-cols-2 gap-2 mb-5 p-4 bg-slate-900/60 border border-slate-700 rounded-lg" data-testid="mfa-recovery-codes">
        {(codes || []).map((c) => (
          <code key={c} className="text-sm text-sky-200 font-mono text-center py-1">{c}</code>
        ))}
      </div>

      <div className="flex gap-2 mb-5">
        <Button type="button" variant="outline" onClick={copy} className="flex-1 border-slate-600 text-slate-200 hover:bg-slate-700" data-testid="mfa-copy-codes">
          {copied ? <Check className="mr-2 h-4 w-4 text-green-400" /> : <Copy className="mr-2 h-4 w-4" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button type="button" variant="outline" onClick={download} className="flex-1 border-slate-600 text-slate-200 hover:bg-slate-700" data-testid="mfa-download-codes">
          <Download className="mr-2 h-4 w-4" /> Download
        </Button>
      </div>

      <Button type="button" onClick={onDone} className="w-full h-11 bg-sky-500 hover:bg-sky-600 text-white" data-testid="mfa-recovery-done">
        I've saved my codes — continue
      </Button>
    </div>
  );
}
